// ─────────────────────────────────────────────────────────────────────────────
// Voice Mode 2.0 — Phase P0 "Visible Spine": UI control runtime.
//
// Framework-agnostic singleton store that owns the SSE connection to
// GET /api/v1/assistant/ui/stream, reconnects with backoff, dispatches plans
// to the executors, sends per-step acks (POST /api/v1/assistant/ui/ack), and
// exposes an immutable state snapshot for React (useSyncExternalStore).
//
// Safety (spec §7–§8 of voice-mode-ui-control-protocol_v1):
//   • commands are NEVER replayed — on reconnect/gap the client aborts
//   • step budget ≤ 12, plan timeout 45 s, per-step waitForAgentId 3 s
//   • dedupe: identical (kind, agentId) 3× within 10 s ⇒ abort
//   • two consecutive failures on one step ⇒ abort
// The feature flag (`voice_ui_control`, DEFAULT OFF on the server) gates the
// whole thing: connect() fetches /config first and stays idle when off.
// ─────────────────────────────────────────────────────────────────────────────

import { executeStep, UiStep, UiStepResult } from './executors';
import type { UiStepKind } from './registry';
import { ensureObservers, isHarnessMode, publishLastPlan, recordTrace } from './harness';

export const API_BASE = '/api/v1';
export const UI_STREAM_URL = `${API_BASE}/assistant/ui/stream`;

// ── Loop guards / budgets (spec §5, §7, §8) ─────────────────────────────────
export const UI_CONTROL_CONSTANTS = {
  MAX_STEPS_PER_PLAN: 12,
  PLAN_TIMEOUT_MS: 45_000,
  /** WCP §2.3: step gap 80–160 ms (readability over speed). */
  STEP_GAP_MS: 140,
  RECONNECT_BASE_MS: 1_000,
  RECONNECT_MAX_MS: 15_000,
  DEDUPE_WINDOW_MS: 10_000,
  DEDUPE_MAX_HITS: 3,
  CAPTION_TTL_MS: 6_000,
} as const;

export type UiRuntimePhase =
  | 'idle' // flag off / not connected
  | 'disabled' // server flag says the feature is off
  | 'connecting'
  | 'connected'
  | 'executing'
  | 'done'
  | 'aborted'
  | 'error';

export interface UiHighlight {
  key: string;
  agentId: string;
  ttlMs: number;
  addedAtMs: number;
}

export interface UiRuntimeState {
  phase: UiRuntimePhase;
  /** Server feature flag state (voice_ui_control). */
  enabled: boolean;
  connected: boolean;
  planId: string | null;
  currentStepId: string | null;
  /** Final spoken line (caption); reasoning is never spoken (spec C7). */
  spoken: string | null;
  /** Overlay caption text (short, human, action narration). */
  caption: string | null;
  highlights: UiHighlight[];
  lastError: string | null;
  lastSeq: number;
}

export interface UiEventEnvelope {
  v?: number;
  id?: string;
  ts?: number;
  seq?: number;
  type?: string;
  planId?: string;
  stepId?: string;
  spoken?: string;
  text?: string;
  final?: boolean;
  reason?: string;
  steps?: UiStep[];
  policy?: { requiresConfirm?: boolean; maxSteps?: number; timeoutMs?: number };
  lastSeq?: number;
  missed?: boolean;
  [key: string]: unknown;
}

export interface UiRuntimeDeps {
  /** SPA navigation (react-router navigate). */
  navigate?: (to: string) => void;
  /** Active workspace id (validated server-side with the same /api guards). */
  getWorkspaceId?: () => string | null;
  /** Test seam: replaces the ack POST. */
  postAck?: (body: UiAckBody) => Promise<void>;
  /** Test seam: replaces the /config flag fetch. */
  fetchConfig?: () => Promise<{ voice_ui_control: boolean }>;
  /** Test seam: shrink the waitForAgentId budget (default 3 s). */
  targetTimeoutMs?: number;
  /** Observability hook for tests/debugging. */
  onEvent?: (event: UiEventEnvelope) => void;
}

export interface UiAckBody {
  plan_id: string;
  step_id: string;
  status: string;
  detail?: string;
  evidence?: Record<string, unknown>;
}

const clientId = (() => {
  // Verification harness: a stable, addressable client id lets the batch runner
  // send plans ONLY to its own browser tab (never to the owner's real browser).
  if (isHarnessMode()) {
    try {
      const override = localStorage.getItem('saik_client_id');
      if (override) return override;
    } catch {
      /* fall through to the random id */
    }
  }
  return `saik-tab-${Math.random().toString(36).slice(2, 10)}`;
})();

export class UiControlRuntime {
  private _state: UiRuntimeState = {
    phase: 'idle',
    enabled: false,
    connected: false,
    planId: null,
    currentStepId: null,
    spoken: null,
    caption: null,
    highlights: [],
    lastError: null,
    lastSeq: 0,
  };
  private readonly listeners = new Set<() => void>();
  private deps: UiRuntimeDeps = {};
  private eventSource: EventSource | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectEpoch = 0;
  /** Cancellation token for the in-flight plan (increment = abort). */
  private planEpoch = 0;
  private readonly dedupeFlags = new Map<string, number[]>();
  private highlightSeq = 0;
  private captionTimer: ReturnType<typeof setTimeout> | null = null;

  // ── store plumbing (useSyncExternalStore compatible) ─────────────

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): UiRuntimeState => this._state;

  private _setState(patch: Partial<UiRuntimeState>): void {
    this._state = { ...this._state, ...patch };
    for (const notify of this.listeners) notify();
  }

  private _addHighlight(agentId: string, ttlMs: number): void {
    const key = `hl-${++this.highlightSeq}`;
    const highlights = [
      ...this._state.highlights,
      { key, agentId, ttlMs, addedAtMs: Date.now() },
    ];
    this._setState({ highlights });
    setTimeout(() => {
      this._setState({
        highlights: this._state.highlights.filter((h) => h.key !== key),
      });
    }, ttlMs);
  }

  private _setCaption(text: string | null): void {
    if (this.captionTimer) clearTimeout(this.captionTimer);
    this._setState({ caption: text });
    if (text) {
      this.captionTimer = setTimeout(
        () => this._setState({ caption: null }),
        UI_CONTROL_CONSTANTS.CAPTION_TTL_MS,
      );
    }
  }

  // ── lifecycle ─────────────────────────────────────────────────────

  init(deps: UiRuntimeDeps): void {
    this.deps = { ...this.deps, ...deps };
    ensureObservers(); // no-op unless localStorage.saik_harness === '1'
  }

  /** Fetch the feature flag; only open the SSE stream when the server has
   *  voice_ui_control ON.  Flag off ⇒ idle forever (zero behaviour change). */
  async connect(): Promise<void> {
    const token = localStorage.getItem('saik_token');
    if (!token) return; // not signed in — nothing to do

    const epoch = ++this.connectEpoch;
    this._setState({ phase: 'connecting' });

    let enabled = false;
    try {
      const config = this.deps.fetchConfig
        ? await this.deps.fetchConfig()
        : await defaultFetchConfig();
      enabled = Boolean(config?.voice_ui_control);
    } catch {
      enabled = false;
    }
    if (epoch !== this.connectEpoch) return; // disconnected meanwhile

    if (!enabled) {
      this._setState({ phase: 'disabled', enabled: false });
      return;
    }
    this._setState({ enabled: true });
    this._openStream(token);
  }

  disconnect(): void {
    this.connectEpoch += 1;
    this.abortPlan('disconnected');
    this.eventSource?.close();
    this.eventSource = null;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.reconnectAttempts = 0;
    this._setState({ phase: 'idle', connected: false });
  }

  // ── SSE wiring ────────────────────────────────────────────────────

  private _openStream(token: string): void {
    const workspaceId = this.deps.getWorkspaceId?.() ?? null;
    const url =
      `${UI_STREAM_URL}?token=${encodeURIComponent(token)}` +
      `&clientId=${encodeURIComponent(clientId)}` +
      (workspaceId ? `&workspace_id=${encodeURIComponent(workspaceId)}` : '');

    let es: EventSource;
    try {
      es = new EventSource(url);
    } catch {
      // Some engines (or test sandboxes) reject the URL — schedule a retry
      // instead of crashing the store.
      this._scheduleReconnect(token);
      return;
    }
    this.eventSource = es;
    const epoch = this.connectEpoch;
    es.onopen = () => {
      this.reconnectAttempts = 0;
      this._setState({ connected: true, phase: this._state.phase === 'executing' ? 'executing' : 'connected' });
    };

    es.addEventListener('ui', (e) => this._onMessage('ui', e as MessageEvent));
    es.addEventListener('card', (e) => this._onMessage('card', e as MessageEvent));
    es.addEventListener('speak', (e) => this._onMessage('speak', e as MessageEvent));

    es.onerror = () => {
      if (epoch !== this.connectEpoch) return;
      this._setState({ connected: false });
      // EventSource auto-retries; force a clean reconnect instead (we must
      // re-hello and never replay commands).
      es.close();
      if (this.eventSource === es) this.eventSource = null;
      this._scheduleReconnect(token);
    };
  }

  private _scheduleReconnect(token: string): void {
    // Backoff: 1s → 2s → 4s … capped at 15s. On reconnect the server emits
    // ui.hello with the current seq; a gap means the client aborts in-flight
    // plans — commands are NEVER replayed (spec §2.1).
    const delay = Math.min(
      UI_CONTROL_CONSTANTS.RECONNECT_BASE_MS * 2 ** this.reconnectAttempts,
      UI_CONTROL_CONSTANTS.RECONNECT_MAX_MS,
    );
    this.reconnectAttempts += 1;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      const current = localStorage.getItem('saik_token');
      if (!current) return; // signed out meanwhile
      this._openStream(current);
    }, delay);
    void token;
  }

  private _onMessage(kind: 'ui' | 'card' | 'speak', e: MessageEvent): void {
    let event: UiEventEnvelope;
    try {
      event = JSON.parse(String(e.data ?? '{}'));
    } catch {
      return;
    }
    this.deps.onEvent?.(event);
    if (typeof event.seq === 'number') {
      this._setState({ lastSeq: event.seq });
    }
    if (kind === 'speak') {
      // FINAL TTS-safe text only (P0: caption; P1 pipes to TTS).
      if (event.type === 'speak' && event.final && event.text) {
        // A correction from the thinking mind interrupts: stop the plan that is
        // in flight so the user hears the correction instead of a stale success
        // (owner design 2026-09-20 — corrections and failed verification interrupt).
        if (event.interrupt === true) {
          this.abortPlan('second_mind');
        }
        this._setState({ spoken: event.text });
        this._setCaption(event.text);
      }
      return;
    }
    if (kind === 'card') {
      // LookupCardStack ships in P2 — note and ignore for now.
      if (event.type === 'card.show') {
        this._setCaption('Details are on your screen.');
      }
      return;
    }
    this._handleUiEvent(event);
  }

  private _handleUiEvent(event: UiEventEnvelope): void {
    switch (event.type) {
      case 'ui.hello': {
        if (event.missed) {
          // Missed a window ⇒ abort anything in flight; commands never replay.
          this.abortPlan('sse_gap');
          this._setCaption('Connection hiccup — repeat that.');
        }
        if (typeof event.lastSeq === 'number') {
          this._setState({ lastSeq: event.lastSeq });
        }
        return;
      }
      case 'ui.plan':
        void this._executePlan(event);
        return;
      case 'ui.commitRequest':
        // P0 has no commit handshake (P2). Surface it as a caption.
        this._setCaption(event.spoken || 'Waiting for your confirmation.');
        return;
      case 'ui.done':
        this.abortPlan('done');
        if (event.spoken) {
          this._setState({ spoken: event.spoken });
          this._setCaption(event.spoken);
        }
        this._setState({ phase: 'done' });
        return;
      case 'ui.abort':
        this.abortPlan(event.reason || 'server_abort');
        this._setCaption(event.spoken || 'Stopped.');
        this._setState({ phase: 'aborted' });
        return;
      default:
        return;
    }
  }

  // ── plan execution (IDLE → EXECUTING → DONE|ABORTED) ──────────────

  private async _executePlan(event: UiEventEnvelope): Promise<void> {
    const planId = String(event.planId || '');
    const steps = Array.isArray(event.steps) ? event.steps : [];
    if (!planId || steps.length === 0) return;

    const budget = event.policy?.maxSteps ?? UI_CONTROL_CONSTANTS.MAX_STEPS_PER_PLAN;
    if (steps.length > budget) {
      this.abortPlan('budget_exceeded');
      this._setState({ phase: 'aborted' });
      return;
    }

    // Only one plan at a time: a new plan supersedes (aborts) the previous.
    this.abortPlan('superseded');
    const epoch = ++this.planEpoch;
    const planStartedAt = Date.now();
    ensureObservers(); // harness mode only; safe re-check
    this._setState({
      phase: 'executing',
      planId,
      currentStepId: null,
      lastError: null,
    });
    if (event.spoken) this._setCaption(event.spoken);
    recordTrace({ type: 'plan.start', planId, at: planStartedAt, data: { stepCount: steps.length, spoken: event.spoken || null } });

    const deadline = Date.now() + (
      isHarnessMode()
        ? 120_000 // harness: long typing runs + page settles; contract is timing-per-step, not total
        : (event.policy?.timeoutMs ?? UI_CONTROL_CONSTANTS.PLAN_TIMEOUT_MS)
    );
    const stepResults: Record<string, unknown>[] = [];
    let consecutiveFailures = 0;

    for (const step of steps) {
      if (epoch !== this.planEpoch) return; // aborted
      if (Date.now() > deadline) {
        this.abortPlan('timeout');
        return;
      }
      if (this._dedupeHit(step)) {
        this.abortPlan('dedupe');
        return;
      }

      this._setState({ currentStepId: step.stepId });
      let result: UiStepResult;
      const stepStartedAt = Date.now();
      try {
        result = await executeStep(step, {
          navigate: this.deps.navigate
            ? (to) => this.deps.navigate?.(to)
            : undefined,
          onHighlight: (agentId, ttlMs) => this._addHighlight(agentId, ttlMs),
          settleMs: UI_CONTROL_CONSTANTS.STEP_GAP_MS,
          // Harness mode: heavy pages can take >3 s to render their controls;
          // widen the target wait so the batch measures the contract, not timing luck.
          targetTimeoutMs: isHarnessMode() ? 8000 : this.deps.targetTimeoutMs,
        });
      } catch (err) {
        result = { status: 'error', detail: String(err) };
      }

      if (epoch !== this.planEpoch) return; // aborted mid-step

      stepResults.push({
        stepId: step.stepId,
        kind: step.kind,
        agentId: step.agentId ?? step.to ?? null,
        value: step.value ?? null,
        status: result.status,
        detail: result.detail ?? null,
        durationMs: Date.now() - stepStartedAt,
      });

      await this._ack(planId, step.stepId, result);
      this._recordDedupe(step);

      if (result.status === 'ok') {
        consecutiveFailures = 0;
        continue;
      }
      consecutiveFailures += 1;
      // Fail loudly and stop (spec §1.5): target_missing/timeout/error abort
      // the plan — the server sees the failing ack and drops it too.
      this.abortPlan(result.status);
      this._setCaption(result.detail || 'I stopped — something went wrong.');
      this._setState({ phase: 'aborted', lastError: result.detail || result.status });
      publishLastPlan({ planId, executedAt: planStartedAt, aborted: true, reason: result.status, steps: stepResults });
      recordTrace({ type: 'plan.aborted', planId, at: Date.now(), data: { reason: result.status } });
      return;
    }

    if (epoch !== this.planEpoch) return;
    this._setState({ phase: 'done', currentStepId: null });
    publishLastPlan({ planId, executedAt: planStartedAt, aborted: false, totalMs: Date.now() - planStartedAt, steps: stepResults });
    recordTrace({ type: 'plan.done', planId, at: Date.now(), data: { totalMs: Date.now() - planStartedAt } });
    // The server publishes the final `speak` once acks land (P0 demo shim) —
    // nothing else is voiced here: "only the final line is spoken".
  }

  /** Increment the plan epoch so any in-flight loop stops at its next check. */
  abortPlan(reason: string): void {
    this.planEpoch += 1;
    if (this._state.phase === 'executing') {
      this._setState({ phase: 'aborted', currentStepId: null });
    }
    void reason;
  }

  // ── dedupe / ack ──────────────────────────────────────────────────

  private _dedupeKey(step: UiStep): string {
    // Request steps (screenshot) are legitimately repeated — the agent may look
    // more than once — and they carry no agentId, so they would collide.
    if (step.kind === 'request') return `request:${step.stepId}`;
    return `${step.kind}:${step.agentId ?? ''}`;
  }

  private _dedupeHit(step: UiStep): boolean {
    // Verification harness: relax the loop guard while explicitly enabled via
    // localStorage.saik_harness (batch verification replays similar plans).
    if (isHarnessMode()) return false;
    if (step.kind === 'request') return false;
    const key = this._dedupeKey(step);
    const now = Date.now();
    const hits = (this.dedupeFlags.get(key) || []).filter(
      (t) => now - t < UI_CONTROL_CONSTANTS.DEDUPE_WINDOW_MS,
    );
    this.dedupeFlags.set(key, hits);
    return hits.length >= UI_CONTROL_CONSTANTS.DEDUPE_MAX_HITS;
  }

  private _recordDedupe(step: UiStep): void {
    if (step.kind === 'request') return;
    const key = this._dedupeKey(step);
    const hits = this.dedupeFlags.get(key) || [];
    hits.push(Date.now());
    this.dedupeFlags.set(key, hits.slice(-UI_CONTROL_CONSTANTS.DEDUPE_MAX_HITS));
  }

  private async _ack(planId: string, stepId: string, result: UiStepResult): Promise<void> {
    const body: UiAckBody = {
      plan_id: planId,
      step_id: stepId,
      status: result.status,
      detail: result.detail,
      evidence: result.evidence,
    };
    try {
      if (this.deps.postAck) await this.deps.postAck(body);
      else await defaultPostAck(body);
    } catch {
      // Ack is best-effort (the failing step still aborts locally); one retry.
      try {
        if (this.deps.postAck) await this.deps.postAck(body);
        else await defaultPostAck(body);
      } catch {
        /* drop — server drops plans without acks anyway */
      }
    }
  }
}

// ── default IO (override in tests) ──────────────────────────────────────────

async function defaultFetchConfig(): Promise<{ voice_ui_control: boolean }> {
  const token = localStorage.getItem('saik_token');
  const resp = await fetch(`${API_BASE}/assistant/ui/config`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!resp.ok) throw new Error(`ui config ${resp.status}`);
  return resp.json();
}

async function defaultPostAck(body: UiAckBody): Promise<void> {
  const token = localStorage.getItem('saik_token');
  const resp = await fetch(`${API_BASE}/assistant/ui/ack`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(`ack ${resp.status}`);
}

// ── module singleton ─────────────────────────────────────────────────────────

let _runtime: UiControlRuntime | null = null;

export function getUiControlRuntime(): UiControlRuntime {
  if (!_runtime) _runtime = new UiControlRuntime();
  return _runtime;
}

/** Test helper: a brand-new runtime (module singleton untouched). */
export function createUiControlRuntime(): UiControlRuntime {
  return new UiControlRuntime();
}

export type { UiStepKind, UiStep };